/**
 * DevelopmentContent Component
 * Displays content blocks for the selected category
 */

import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type { BabyDevelopmentCategory, BabyDevelopmentContent as ContentType } from '../types';
import { DevelopmentTabs } from './DevelopmentTabs';
import { ContentBlock } from './ContentBlock';

interface DevelopmentContentProps {
  categories: BabyDevelopmentCategory[];
  content: ContentType[];
  isLoading?: boolean;
}

export function DevelopmentContent({
  categories,
  content,
  isLoading,
}: DevelopmentContentProps) {
  const { t } = useTranslation('babyDevelopment');
  const [activeCategory, setActiveCategory] = useState<string>(
    categories[0]?.slug || 'motorik'
  );

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-white" />
          <h3 className="text-white text-xl font-bold">{t('development.title')}</h3>
        </div>
        <div className="h-12 bg-white/10 rounded-[15px] mb-4 animate-pulse" />
        <div className="space-y-3">
          <div className="h-4 bg-white/10 rounded w-full animate-pulse" />
          <div className="h-4 bg-white/10 rounded w-3/4 animate-pulse" />
          <div className="h-4 bg-white/10 rounded w-5/6 animate-pulse" />
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  // Find content for active category
  const activeContent = content.find((c) => {
    const category = categories.find((cat) => cat.id === c.category_id);
    return category?.slug === activeCategory;
  });

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-white" />
        <h3 className="text-white text-xl font-bold">{t('development.title')}</h3>
      </div>

      {/* Category Tabs */}
      <DevelopmentTabs
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      {/* Content */}
      <div className="mt-5 space-y-6">
        {activeContent?.content_blocks && activeContent.content_blocks.length > 0 ? (
          activeContent.content_blocks.map((block, index) => (
            <ContentBlock key={index} block={block} />
          ))
        ) : (
          <p className="text-white/50 text-center py-4">
            {t('development.noContent')}
          </p>
        )}
      </div>
    </div>
  );
}
